import { useEffect, useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type Count = { language: string; reason: string; detail: string; count: number; oldest_seen_at: string; last_seen_at: string };
type Progress = { operation: string; language: string; last_completed_at: string | null; last_error: string | null;
  last_result: { counts: Record<string, number> } | null; current_counts: Record<string, number> };
type Observation = { translation_id: string; language: string; detail: string; missing_sections: string[]; last_seen_at: string };
type Plan = { id: string; language: string; phase: string; last_result: { held?: string;
  execution_failure?: { attempts: number; held: boolean; error: string };
  residual?: { done: number; pending: number; held: number; refused: number } } | null };
type Status = { scope: string; progress: Progress[]; counts: Count[]; observations: Observation[]; plans: Plan[] };
const date = (value: string | null) => value ? new Date(value).toLocaleString() : 'No completed scan';
const counts = (value: Record<string, number>) => Object.entries(value).map(([key, count]) => `${key.replace(/_/g, ' ')}: ${count}`).join(', ') || 'No rows observed';
const planResult = (plan: Plan) => {
  const failure = plan.last_result?.execution_failure;
  if (failure) return `${failure.held ? 'Execution held; operator review required' : 'Retry pending'} after ${failure.attempts} failure(s). Last error: ${failure.error}`;
  return plan.last_result?.held || (plan.last_result?.residual ? counts(plan.last_result.residual) : 'Awaiting repair execution');
};

function nextStep(row: Count) {
  if (row.detail.startsWith('recovery_candidate_failed:')) return 'Review this recovery error. A fresh catalog scan must validate the row before retry.';
  if (row.reason === 'normal_claimable') return 'Waiting for the normal translation worker.';
  if (row.reason === 'terminal_section_recoverable') return 'Recovery will recheck this row before queuing its missing sections.';
  if (row.reason === 'provider_active' || row.detail.includes('reconciliation') || row.detail === 'too_recent') return 'Wait for the active attempt and its results to settle.';
  if (row.detail.includes('paused') || row.detail === 'language_disabled') return 'Review the language configuration and pause decision.';
  if (row.detail.startsWith('source_')) return 'Review the current source page and its approval.';
  if (row.detail === 'feed_unavailable') return 'Review product availability in the catalog feed.';
  return 'Operator review required before another translation attempt.';
}

export function TranslationRecoveryTab({ siteId }: { siteId: string }) {
  const [data, setData] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true); setData(null); setError(null);
    (async () => {
      try {
        const response = await authFetch(`/api/aeo-translations/recovery/${siteId}`, { signal: controller.signal, timeout: 20000 });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || `Recovery request failed (${response.status})`);
        if (active) setData(result);
      } catch (failure) { if (active) setError(failure instanceof Error ? failure.message : String(failure)); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; controller.abort(); };
  }, [siteId, refresh]);
  return <Card>
    <CardHeader>
      <CardTitle>Translation recovery</CardTitle>
      <CardDescription>Scans run in bounded pages. Dates show when a row or language was last checked; a completed scan does not establish current catalog closure.</CardDescription>
      <Button variant="outline" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Refresh recovery status</Button>
    </CardHeader>
    <CardContent className="space-y-6">
      {loading && <p role="status">Loading recovery status…</p>}
      {error && <p role="alert">{error}</p>}
      {data && <>
        <p className="text-sm text-muted-foreground">{data.scope}</p>
        <h3 className="font-semibold">Catalog scans</h3>
        {!data.progress.length && <p>No recovery scans recorded yet.</p>}
        <Table><TableHeader><TableRow><TableHead>Language / scan</TableHead><TableHead>Last completed pass</TableHead><TableHead>Observed counts</TableHead><TableHead>Current pass / error</TableHead></TableRow></TableHeader>
          <TableBody>{data.progress.map(row => <TableRow key={`${row.operation}-${row.language}`}>
            <TableCell>{row.language} / {row.operation === 'translation' ? 'Translation rows' : 'Product comparison values'}</TableCell>
            <TableCell>{date(row.last_completed_at)}</TableCell><TableCell>{row.last_result ? counts(row.last_result.counts) : 'No completed scan'}</TableCell>
            <TableCell>{row.last_error || counts(row.current_counts)}</TableCell>
          </TableRow>)}</TableBody></Table>
        <h3 className="font-semibold">Outstanding observations</h3>
        {!data.counts.length && <p>No outstanding observations recorded. Check scan coverage above.</p>}
        <Table><TableHeader><TableRow><TableHead>Language</TableHead><TableHead>Reason / rows</TableHead><TableHead>First / last observed</TableHead><TableHead>Next step</TableHead></TableRow></TableHeader>
          <TableBody>{data.counts.map(row => <TableRow key={`${row.language}-${row.reason}-${row.detail}`}>
            <TableCell>{row.language}</TableCell><TableCell>{row.detail.replace(/_/g, ' ')} / {row.count}</TableCell>
            <TableCell>{date(row.oldest_seen_at)} / {date(row.last_seen_at)}</TableCell><TableCell>{nextStep(row)}</TableCell>
          </TableRow>)}</TableBody></Table>
        <h3 className="font-semibold">Comparison repairs (latest 100 plans)</h3>
        {data.plans.map(plan => <p key={plan.id} className="text-sm">{plan.language}: {plan.phase}; {planResult(plan)}<br /><span className="text-muted-foreground">{plan.id}</span></p>)}
        <details><summary>Oldest 100 outstanding translation rows</summary>
          {data.observations.map(row => <p key={row.translation_id} className="my-2 text-sm">{row.translation_id} · {row.language} · {row.detail.replace(/_/g, ' ')}<br />Missing sections: {row.missing_sections.join(', ') || 'None recorded'} · Observed {date(row.last_seen_at)}</p>)}
        </details>
      </>}
    </CardContent>
  </Card>;
}
